import { Image } from '@components/gassan-ui'
import { Article } from '@generated'
import { cn } from '@lib/cn'
import { formatPrice } from '@lib/formatPrice'
import { imgix } from '@lib/imigx'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { FC } from 'react'
import slugify from 'slugify'
import { getRcpoModelPrimaryAttributes } from '../../../lib/get-rcpo-model-primary-attributes'
import { Text } from '@components/rolex-ui/Text'
import { getRCPOProductAltText } from '@lib/get-descriptive-product-alt-text'

type ProductCardProps = {
  data: Article
}

export const ProductCard: FC<ProductCardProps> = ({ data }) => {
  const { t } = useTranslation('navigation')

  const primaryAttributes = getRcpoModelPrimaryAttributes(data)
  const slug = `${slugify(data.modelName, { lower: true })}-${data.id}`

  return (
    <Link
      href={`${t('rcpo.selection.url')}/${slug}`}
      passHref
      className={cn(
        'group col-span-3 flex h-full w-full flex-1 flex-col',
        'md:col-span-3 md:gap-0',
        'lg:col-span-3',
        'rounded focus:outline-none focus:ring-2 focus:ring-rolexGreenAlt',
      )}
      aria-label={`View details for ${data.modelName}`}
    >
      <Image
        sources={[
          imgix(data.images[0], { width: 350 }),
          imgix(data.images[0], { width: 500 }),
          imgix(data.images[0], { width: 650 }),
        ]}
        alt={getRCPOProductAltText(data, `Rolex Certified Pre-Owned ${data.modelName} watch`)}
        className="aspect-square w-full transition-opacity duration-150 group-hover:opacity-80"
      />
      <div
        className={cn(
          'bg-black px-5 pb-[30px] pt-5',
          'md:px-[30px] md:pb-10 md:pt-[30px]',
          'lg:p-[50px]',
          'flex flex-1 flex-col',
        )}
      >
        <Text variant="legend16" className="text-white">
          Rolex Certified Pre-Owned
        </Text>
        <Text variant="body24" className="my-[5px] text-white">
          {data.modelName}
        </Text>
        <Text variant="legend16" weight="light" as="div" className="text-white">
          {primaryAttributes}
        </Text>
        <Text variant="legend16" weight="light" as="div" className="text-white">
          {formatPrice(data.price, false, 'right')}
        </Text>
      </div>
    </Link>
  )
}
